<script lang="ts">
  import { clone } from 'lodash-es'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { moveArrayElement } from '#app/lib/utils'
  import Spinner from '#general/components/spinner.svelte'
  import { updateElement, type ListingElementWithEntity } from '#listings/lib/listings'
  import type { ListingElement } from '#server/types/element'
  import { i18n } from '#user/lib/i18n'

  let {
    element,
    elements = $bindable(),
    flash = $bindable(),
    index,
  }: {
    element: ListingElementWithEntity
    elements: ListingElement[]
    flash?: FlashState
    index: number
  } = $props()

  let isReordering = $state(false)
  let reorderPromise: Promise<unknown> | null = $state()

  async function waitingForReorder (newIndex: number) {
    reorderPromise = reorder(newIndex)
    await reorderPromise
    reorderPromise = null
  }

  async function reorder (newIndex: number) {
    isReordering = true
    flash = null
    const oldElements = clone(elements)
    elements = moveArrayElement(elements, index, newIndex)
    const exclusiveOrdinal = newIndex + 1
    try {
      await updateElement({
        id: element._id,
        ordinal: exclusiveOrdinal,
      })
    } catch (err) {
      flash = err
      elements = oldElements
    } finally {
      isReordering = false
    }
  }

  const hasSeveralElements = $derived(elements.length > 1)
</script>

{#if hasSeveralElements}
  <div class="reorder-section">
    {#if index !== 0}
      <button
        disabled={isReordering}
        onclick={() => waitingForReorder(index - 1)}
        title={i18n('Move up')}
      >
        {@html icon('chevron-up')}
      </button>
    {:else}
      <span class="button-placeholder"></span>
    {/if}
    <div class="list-index">
      {#if reorderPromise}
        <Spinner />
      {:else}
        {index + 1}
      {/if}
    </div>
    {#if index !== elements.length - 1}
      <button
        disabled={isReordering}
        onclick={() => waitingForReorder(index + 1)}
        title={i18n('Move down')}
      >
        {@html icon('chevron-down')}
      </button>
    {:else}
      <span class="button-placeholder"></span>
    {/if}
  </div>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .reorder-section{
    @include display-flex(column, center);
    padding: 1em;
    button{
      color: $grey;
    }
  }
  .list-index{
    font-size: 1.3em;
  }
  .button-placeholder{
    height: 1.3em;
  }
</style>
